import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import styled from "styled-components";
import { toast, Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { I18nProvider } from "./contexts/I18nContext";
import ValidationModal from "./components/ValidationModal";
import "./i18n";

import Navigation from "./components/layout/Navigation";
import LoginForm from "./components/auth/LoginForm";
import SignupForm from "./components/auth/SignupForm";
import ProfilePage from "./components/user/ProfilePage";
import SettingsPage from "./components/user/SettingsPage";
import UserLimitsDisplay from "./components/user/UserLimitsDisplay";
import AdminDashboard from "./components/admin/AdminDashboard";
import ApproverDashboard from "./components/admin/ApproverDashboard";
import Dashboard from "./components/dashboard/Dashboard";
import MapComponent from "./components/MapComponent";
import MapGallery from "./components/social/MapGallery";
import StatePanel from "./components/StatePanel";
import CommentsSection from "./components/social/CommentsSection";
import TemplateExportModal from "./components/TemplateExportModal";
import HomePage from "./components/HomePage";
import ContactPage from "./components/pages/ContactPage";
import FAQPage from "./components/pages/FAQPage";
import ChangelogPage from "./components/pages/ChangelogPage";
import PrivacyPolicyPage from "./components/pages/PrivacyPolicyPage";
import TermsOfServicePage from "./components/pages/TermsOfServicePage";
import SEOHead from "./components/SEOHead";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { State, GuideLayer, MapData, MapStyle, MAP_STYLES, RegionCode, Coordinates } from "./types";
import {
  getMapById,
  likeMap,
  unlikeMap,
  deleteMap,
  requestMapApproval,
  updateMap,
  createMap,
  uploadMapThumbnail,
} from "./services/socialService";
import { getRegionBorder } from "./data/borders";
import { prepareThumbnailForUpload } from "./utils/imageUtils";
import { validateMapSchema, getValidationMessage } from "./utils/schemaValidator";
import "./index.css";

const AppContainer = styled.div`
  min-height: 100vh;
  background: var(--background-color);
  color: var(--text-color);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  overflow-y: auto;
  background: var(--background-color);
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const AuthContainer = styled.div`
  min-height: calc(100vh - 70px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: var(--background-color);
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const MapContainer = styled.div<{ $isPanelOpen: boolean }>`
  display: flex;
  height: calc(100vh - 70px);
  overflow: hidden;
  flex-direction: column;
  background: var(--background-color);
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --panel-width: ${(props) => (props.$isPanelOpen ? "350px" : "0px")};

  @media (min-width: 1024px) {
    flex-direction: row;
  }

  /* When panel is closed on desktop, ensure map takes full width */
  @media (min-width: 1024px) {
    ${(props) =>
      !props.$isPanelOpen &&
      `
      .map-area {
        width: 100% !important;
        flex: 1 1 100% !important;
      }
    `}
  }
`;

const Sidebar = styled.div<{ $isOpen: boolean }>`
  width: 100%;
  border-top: 1px solid var(--border-color);
  box-shadow: 0 -2px 10px var(--shadow-color);
  max-height: ${(props) => (props.$isOpen ? "50vh" : "0")};
  overflow-y: auto;
  background: var(--surface-color);
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    visibility 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};

  @media (min-width: 1024px) {
    width: ${(props) => (props.$isOpen ? "350px" : "0")};
    border-top: none;
    border-left: 1px solid var(--border-color);
    box-shadow: -2px 0 10px var(--shadow-color);
    max-height: none;
    overflow: hidden;
    position: ${(props) => (props.$isOpen ? "static" : "absolute")};
    right: ${(props) => (props.$isOpen ? "auto" : "-350px")};
    top: 0;
    bottom: 0;
    z-index: ${(props) => (props.$isOpen ? "auto" : "-1")};
  }
`;

// Modern View Panel - Matching StatePanel design
const ViewPanelContainer = styled.div<{
  $isDarkMode: boolean;
  $isOpen: boolean;
}>`
  width: 100%;
  border-top: 1px solid
    ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  max-height: ${(props) => (props.$isOpen ? "50vh" : "0")};
  overflow-y: auto;
  background: ${(props) => (props.$isDarkMode ? "#1a1a1a" : "#ffffff")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    visibility 0.3s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease,
    color 0.3s ease;
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  @media (min-width: 1024px) {
    width: ${(props) => (props.$isOpen ? "350px" : "0")};
    border-top: none;
    border-left: 1px solid
      ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    max-height: none;
    overflow: hidden;
    position: ${(props) => (props.$isOpen ? "static" : "absolute")};
    right: ${(props) => (props.$isOpen ? "auto" : "-350px")};
    top: 0;
    bottom: 0;
    z-index: ${(props) => (props.$isOpen ? "auto" : "-1")};
  }
`;

const ViewPanelContent = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${(props) => (props.$isDarkMode ? "#1a1a1a" : "#ffffff")};
`;

const ViewTabsContainer = styled.div<{ $isDarkMode: boolean }>`
  display: flex;
  border-bottom: 1px solid
    ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
  background: ${(props) => (props.$isDarkMode ? "#1a1a1a" : "#ffffff")};
  padding: 0;
  gap: 0;
  flex-shrink: 0;
`;

const ViewTabButton = styled.button<{
  $isActive: boolean;
  $isDarkMode: boolean;
}>`
  padding: 12px 20px;
  border: none;
  background: transparent;
  color: ${(props) =>
    props.$isActive
      ? props.$isDarkMode
        ? "#ffffff"
        : "#1a1a1a"
      : props.$isDarkMode
      ? "#b0b0b0"
      : "#666"};
  font-size: 14px;
  font-weight: ${(props) => (props.$isActive ? "600" : "500")};
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.$isActive ? "#2196f3" : "transparent")};
  transition: all 0.2s ease;
  position: relative;
  margin-bottom: -1px;
  flex: 1;

  &:hover {
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
    background: ${(props) =>
      props.$isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)"};
  }
`;

const ViewTabContent = styled.div<{ $isDarkMode: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
`;

const ViewSection = styled.div<{ $isDarkMode: boolean; $compact?: boolean }>`
  margin-bottom: ${(props) => (props.$compact ? "16px" : "24px")};
  padding-bottom: ${(props) => (props.$compact ? "12px" : "20px")};
  border-bottom: 1px solid
    ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#e0e0e0")};
  transition: border-color 0.3s ease;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const ViewSectionHeader = styled.div<{
  $isDarkMode: boolean;
  $compact?: boolean;
  $clickable?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: ${(props) => (props.$compact ? "8px" : "16px")};
  font-size: ${(props) => (props.$compact ? "13px" : "14px")};
  font-weight: 600;
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
  transition: color 0.3s ease;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  user-select: none;

  &:hover {
    color: ${(props) =>
      props.$clickable ? (props.$isDarkMode ? "#b0b0b0" : "#666") : undefined};
  }
`;

const ViewSectionChevron = styled.div<{
  $isExpanded: boolean;
  $isDarkMode: boolean;
}>`
  margin-left: auto;
  transition: transform 0.2s ease;
  transform: ${(props) =>
    props.$isExpanded ? "rotate(180deg)" : "rotate(0deg)"};
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#666")};
  font-size: 12px;
`;

const ViewSectionIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const ViewMapTitle = styled.h2<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#1a1a1a")};
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  transition: color 0.3s ease;
`;

const ViewMapDescription = styled.p<{ $isDarkMode: boolean }>`
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#666")};
  margin: 0 0 16px 0;
  line-height: 1.6;
  font-size: 13px;
  transition: color 0.3s ease;
`;

const ViewUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 8px 0;
`;

const ViewUserAvatar = styled.div<{ $avatar?: string; $isDarkMode: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) =>
    props.$avatar
      ? `url(${props.$avatar})`
      : props.$isDarkMode
      ? "#2196f3"
      : "#2196f3"};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

const ViewUserName = styled.span<{ $isDarkMode: boolean }>`
  font-weight: 500;
  color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#666")};
  font-size: 13px;
  transition: color 0.3s ease;
`;

const ViewButtonGroup = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? "6px" : "8px")};
  margin-top: ${(props) => (props.$compact ? "8px" : "16px")};
`;

const ViewToggleContainer = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => (props.$compact ? "6px 0" : "12px 0")};
`;

const ViewToggleLabel = styled.label<{
  $isDarkMode: boolean;
  $compact?: boolean;
}>`
  font-size: ${(props) => (props.$compact ? "13px" : "14px")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  cursor: pointer;
  flex: 1;
  transition: color 0.3s ease;
`;

const ViewToggleSwitch = styled.label<{
  $checked: boolean;
  $isDarkMode?: boolean;
  $compact?: boolean;
}>`
  position: relative;
  width: ${(props) => (props.$compact ? "40px" : "44px")};
  height: ${(props) => (props.$compact ? "20px" : "24px")};
  background: ${(props) =>
    props.$checked ? "#2196f3" : props.$isDarkMode ? "#404040" : "#ccc"};
  border-radius: ${(props) => (props.$compact ? "10px" : "12px")};
  cursor: pointer;
  transition: background 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    width: ${(props) => (props.$compact ? "16px" : "18px")};
    height: ${(props) => (props.$compact ? "16px" : "18px")};
    border-radius: 50%;
    background: white;
    top: ${(props) => (props.$compact ? "2px" : "3px")};
    left: ${(props) =>
      props.$checked
        ? props.$compact
          ? "22px"
          : "23px"
        : props.$compact
        ? "2px"
        : "3px"};
    transition: left 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const ViewToggleInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const ViewPrimaryButton = styled.button<{ $isDarkMode: boolean }>`
  padding: 10px 16px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: #1976d2;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ViewSecondaryButton = styled.button<{ $isDarkMode: boolean }>`
  padding: 10px 16px;
  background: ${(props) => (props.$isDarkMode ? "#2d2d2d" : "#f5f5f5")};
  color: ${(props) => (props.$isDarkMode ? "#e0e0e0" : "#1a1a1a")};
  border: 1px solid ${(props) => (props.$isDarkMode ? "#404040" : "#d0d0d0")};
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: ${(props) => (props.$isDarkMode ? "#404040" : "#e8e8e8")};
    border-color: ${(props) => (props.$isDarkMode ? "#505050" : "#b0b0b0")};
  }
`;

const ViewDangerButton = styled.button<{ $isDarkMode: boolean }>`
  padding: 10px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ViewStatusBadge = styled.div<{
  $variant: "success" | "warning" | "danger" | "info" | "default";
  $isDarkMode: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => {
    switch (props.$variant) {
      case "success":
        return props.$isDarkMode
          ? "rgba(76, 175, 80, 0.2)"
          : "rgba(76, 175, 80, 0.1)";
      case "warning":
        return props.$isDarkMode
          ? "rgba(255, 152, 0, 0.2)"
          : "rgba(255, 152, 0, 0.1)";
      case "danger":
        return props.$isDarkMode
          ? "rgba(244, 67, 54, 0.2)"
          : "rgba(244, 67, 54, 0.1)";
      case "info":
        return props.$isDarkMode
          ? "rgba(33, 150, 243, 0.2)"
          : "rgba(33, 150, 243, 0.1)";
      default:
        return props.$isDarkMode ? "#2d2d2d" : "#f5f5f5";
    }
  }};
  color: ${(props) => {
    switch (props.$variant) {
      case "success":
        return props.$isDarkMode ? "#81c784" : "#4caf50";
      case "warning":
        return props.$isDarkMode ? "#ffb74d" : "#ff9800";
      case "danger":
        return props.$isDarkMode ? "#ef5350" : "#f44336";
      case "info":
        return props.$isDarkMode ? "#64b5f6" : "#2196f3";
      default:
        return props.$isDarkMode ? "#b0b0b0" : "#666";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$variant) {
        case "success":
          return props.$isDarkMode
            ? "rgba(76, 175, 80, 0.3)"
            : "rgba(76, 175, 80, 0.2)";
        case "warning":
          return props.$isDarkMode
            ? "rgba(255, 152, 0, 0.3)"
            : "rgba(255, 152, 0, 0.2)";
        case "danger":
          return props.$isDarkMode
            ? "rgba(244, 67, 54, 0.3)"
            : "rgba(244, 67, 54, 0.2)";
        case "info":
          return props.$isDarkMode
            ? "rgba(33, 150, 243, 0.3)"
            : "rgba(33, 150, 243, 0.2)";
        default:
          return props.$isDarkMode ? "#404040" : "#d0d0d0";
      }
    }};
`;

const SocialSidebar = ViewPanelContainer;

// Map Style Gallery Components (for view page)
const MapStyleGallery = styled.div<{ $compact?: boolean }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${(props) => (props.$compact ? "80px" : "100px")}, 1fr)
  );
  gap: ${(props) => (props.$compact ? "8px" : "10px")};
  margin-top: ${(props) => (props.$compact ? "4px" : "8px")};
`;

const MapStyleCard = styled.button<{
  $isSelected: boolean;
  $isDarkMode: boolean;
  $color: string;
  $compact?: boolean;
}>`
  position: relative;
  width: 100%;
  aspect-ratio: ${(props) => (props.$compact ? "3/2" : "4/3")};
  border: ${(props) => (props.$compact ? "1.5px" : "2px")} solid
    ${(props) =>
      props.$isSelected
        ? "#2196f3"
        : props.$isDarkMode
        ? "#2d2d2d"
        : "#d0d0d0"};
  border-radius: ${(props) => (props.$compact ? "6px" : "8px")};
  background: ${(props) => props.$color};
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  box-shadow: ${(props) =>
    props.$isSelected
      ? "0 0 0 3px rgba(33, 150, 243, 0.2)"
      : "0 1px 3px rgba(0, 0, 0, 0.1)"};

  &:hover {
    border-color: ${(props) =>
      props.$isSelected
        ? "#2196f3"
        : props.$isDarkMode
        ? "#404040"
        : "#b0b0b0"};
    transform: translateY(-2px);
    box-shadow: ${(props) =>
      props.$isSelected
        ? "0 0 0 3px rgba(33, 150, 243, 0.3)"
        : "0 4px 8px rgba(0, 0, 0, 0.15)"};
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0, 0, 0, 0.3) 60%,
      rgba(0, 0, 0, 0.6) 100%
    );
    pointer-events: none;
  }

  ${(props) =>
    props.$isSelected &&
    `
    &::after {
      content: '✓';
      position: absolute;
      top: 6px;
      right: 6px;
      width: 20px;
      height: 20px;
      background: #2196f3;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
      z-index: 2;
    }
  `}
`;

const MapStylePreview = styled.div<{ $color: string; $thumbnail?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) => props.$color};
  background-image: ${(props) =>
    props.$thumbnail
      ? `url(${props.$thumbnail})`
      : props.$color.includes("#1") || props.$color.includes("#2")
      ? "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.05) 0%, transparent 50%)"
      : "radial-gradient(circle at 20% 30%, rgba(0,0,0,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.03) 0%, transparent 50%)"};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
`;

const MapStyleLabel = styled.span<{ $isDarkMode: boolean; $compact?: boolean }>`
  position: relative;
  z-index: 1;
  color: white;
  font-size: ${(props) => (props.$compact ? "10px" : "11px")};
  font-weight: 600;
  text-align: center;
  padding: ${(props) => (props.$compact ? "3px 5px" : "4px 6px")};
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PanelToggleButton = styled.button<{
  $isOpen: boolean;
  $isMobile: boolean;
}>`
  position: fixed;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: ${(props) =>
    props.$isMobile ? "8px 8px 0 0" : "8px 0 0 8px"};
  padding: 8px 12px;
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-color);
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px var(--shadow-color);

  &:hover {
    background: var(--background-color);
    color: var(--primary-color);
  }

  /* Mobile positioning */
  ${(props) =>
    props.$isMobile &&
    `
    bottom: ${props.$isOpen ? "50vh" : "0"};
    left: 50%;
    transform: translateX(-50%);
    transition: bottom 0.3s ease;
  `}

  /* Desktop positioning with drawer-like appearance */
  ${(props) =>
    !props.$isMobile &&
    `
    top: 50%;
    right: ${props.$isOpen ? "350px" : "0"};
    transform: translateY(-50%);
    transition: right 0.3s ease;
    
    /* Add a subtle inner shadow to create depth and drawer feel */
    box-shadow: 
      inset -2px 0 4px rgba(0, 0, 0, 0.1),
      0 2px 8px var(--shadow-color);
    
    /* Add a subtle border on the left to create separation */
    border-left: 2px solid var(--border-color);
    
    /* Position it slightly overlapping the edge to suggest it's attached */
    right: ${props.$isOpen ? "350px" : "-2px"};
  `}

  @media (max-width: 1023px) {
    display: ${(props) => (props.$isMobile ? "flex" : "none")};
  }

  @media (min-width: 1024px) {
    display: ${(props) => (props.$isMobile ? "none" : "flex")};
  }
`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const MapArea = styled.div<{ $isPanelOpen: boolean }>`
  flex: 1;
  position: relative;
  background: var(--background-color);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 0; /* Allow flex item to shrink below content size */

  /* Mobile: When panel is closed, map takes full height */
  @media (max-width: 1023px) {
    height: ${(props) =>
      props.$isPanelOpen
        ? "calc(100vh - 70px - 50vh - 1px)"
        : "calc(100vh - 70px)"};
  }

  /* Desktop: When panel is closed, map takes full width */
  @media (min-width: 1024px) {
    width: ${(props) => (props.$isPanelOpen ? "auto" : "100%")};
  }
`;

// Route Protection Components
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Loading...</p>
        </div>
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <AuthContainer>
        <LoginForm />
      </AuthContainer>
    );
  }

  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Loading...</p>
        </div>
      </PageContainer>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <PageContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <h1>Access Denied</h1>
          <p>You don't have permission to access this page.</p>
        </div>
      </PageContainer>
    );
  }

  return <>{children}</>;
};

const ApproverRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Loading...</p>
        </div>
      </PageContainer>
    );
  }

  if (!user || (user.role !== "approver" && user.role !== "admin")) {
    return (
      <PageContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <h1>Access Denied</h1>
          <p>You don't have permission to access this page.</p>
        </div>
      </PageContainer>
    );
  }

  return <>{children}</>;
};

// Page Components

const ExplorePage: React.FC = () => {
  return (
    <PageContainer>
      <MapGallery />
    </PageContainer>
  );
};

const MyMapsPage: React.FC = () => {
  return (
    <PageContainer>
      <UserLimitsDisplay />
      <MapGallery
        showApprovalStatus={true}
        showCreateMapCard={true}
        hideEmptyState={true}
        showUserMapsOnly={true}
      />
    </PageContainer>
  );
};

// Auth Page Component
const AuthPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialIsLogin = location.pathname === "/login";
  const [isLogin, setIsLogin] = useState(initialIsLogin);

  useEffect(() => {
    setIsLogin(location.pathname === "/login");
  }, [location.pathname]);

  // Redirect to dashboard if user is authenticated
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Don't render auth forms if user is authenticated
  if (user) {
    return null;
  }

  return (
    <AuthContainer>{isLogin ? <LoginForm /> : <SignupForm />}</AuthContainer>
  );
};

// Create Map Page Component
const CreateMapPage: React.FC = () => {
  const { user, refreshUserMapsCount, canCreateMap } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Get edit parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const editMapId = searchParams.get("edit");

  // State to track mapId for version history (updates immediately after creation)
  const [currentMapId, setCurrentMapId] = useState<string | null>(editMapId);

  // Update currentMapId when editMapId changes (from URL)
  useEffect(() => {
    setCurrentMapId(editMapId);
  }, [editMapId]);

  const [states, setStates] = useState<State[]>([]);
  const [guideLayers, setGuideLayers] = useState<GuideLayer[]>([]);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [drawingMode, setDrawingMode] = useState(false);
  const [editingStateId, setEditingStateId] = useState<string | null>(null);
  const [showEditingInstructions, setShowEditingInstructions] = useState(true);
  const [mapName, setMapName] = useState("");
  const [mapDescription, setMapDescription] = useState("");
  const [mapRegion, setMapRegion] = useState<RegionCode>("iran");
  const [showBorder, setShowBorder] = useState(false); // Default to false - Mapbox's borders are more accurate
  const [customBorder, setCustomBorder] = useState<Coordinates[][] | null>(null); // User's custom border
  const [isEditingBorder, setIsEditingBorder] = useState(false);


  // Region viewport data for zooming/centering when region changes
  const REGION_VIEWPORTS: Record<RegionCode, { center: { lat: number; lng: number }; zoom: number }> = {
    iran: { center: { lat: 32.4279, lng: 53.6880 }, zoom: 5 },
    iraq: { center: { lat: 33.2232, lng: 43.6793 }, zoom: 6 },
    syria: { center: { lat: 34.8021, lng: 38.9968 }, zoom: 6 },
    kurdistan: { center: { lat: 36.2419, lng: 43.7515 }, zoom: 6 },
    afghanistan: { center: { lat: 33.9391, lng: 67.7100 }, zoom: 6 },
    baluchistan: { center: { lat: 27.5, lng: 64.0 }, zoom: 6 },
    turkey: { center: { lat: 38.9637, lng: 35.2433 }, zoom: 5 },
    armenia: { center: { lat: 40.0691, lng: 45.0382 }, zoom: 7 },
    azerbaijan: { center: { lat: 40.1431, lng: 47.5769 }, zoom: 7 },
    turkmenistan: { center: { lat: 38.9697, lng: 59.5563 }, zoom: 6 },
    pakistan: { center: { lat: 30.3753, lng: 69.3451 }, zoom: 5 },
    custom: { center: { lat: 32.0, lng: 53.0 }, zoom: 5 },
  };

  // Handler for region change - zooms map to new region
  const handleRegionChange = (newRegion: RegionCode) => {
    setMapRegion(newRegion);
    
    // Fly to the new region
    const viewport = REGION_VIEWPORTS[newRegion];
    if (mapRef.current && viewport) {
      mapRef.current.flyTo({
        center: [viewport.center.lng, viewport.center.lat],
        zoom: viewport.zoom,
        duration: 1500,
      });
    }
  };

  const [isPublic, setIsPublic] = useState(false);
  // Approval status - passed to StatePanel for display
  const [approvalRequested, setApprovalRequested] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<string | undefined>(
    undefined
  );
  const [version, setVersion] = useState<number | undefined>(undefined);
  const [approvedVersion, setApprovedVersion] = useState<number | undefined>(
    undefined
  );
  const [showCapitals, setShowCapitals] = useState(true);
  const [showCapitalNames, setShowCapitalNames] = useState(false);
  const [showStateNames, setShowStateNames] = useState(false);
  const [showStateFill, setShowStateFill] = useState(true);
  const [mapStyle, setMapStyle] = useState<MapStyle>("light");
  const [isSettingCapital, setIsSettingCapital] = useState(false);
  const [editingGuideLayerId, setEditingGuideLayerId] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [isTemplateExportModalOpen, setIsTemplateExportModalOpen] =
    useState(false);
  const [currentThumbnail, setCurrentThumbnail] = useState<string | null>(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationIssues, setValidationIssues] = useState<string[]>([]);
  const [validationHasErrors, setValidationHasErrors] = useState(false);

  const [isPanelOpen, setIsPanelOpen] = useState(true);

  const mapRef = useRef<any>(null);
  const statePanelRef = useRef<any>(null);

  // Trigger map resize when panel state changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current && mapRef.current.resize) {
        try {
          mapRef.current.resize();
        } catch (error) {
          // Silently handle resize errors
        }
      }
    }, 300); // Wait for transition to complete

    return () => clearTimeout(timer);
  }, [isPanelOpen]);

  // Load map data if editing
  useEffect(() => {
    if (editMapId) {
      const loadMap = async () => {
        try {
          setLoading(true);
          const mapData = await getMapById(editMapId);
          if (mapData) {
            // Filter out any border states from legacy maps
            const loadedStates = (mapData.states || []).filter(s => !s.isBorder);
            setStates(loadedStates);
            setGuideLayers(mapData.guideLayers || []);
            setMapName(mapData.name);
            setMapDescription(mapData.description || "");
            setIsPublic(mapData.isPublic);
            setApprovalRequested(mapData.approvalRequested || false);
            setIsApproved(mapData.isApproved || false);
            setIsRejected(mapData.isRejected || false);
            setRejectionReason(mapData.rejectionReason);
            setVersion(mapData.version);
            setApprovedVersion(mapData.approvedVersion);
            // Load custom border if it exists
            if (mapData.customBorder) {
              setCustomBorder(mapData.customBorder);
              setShowBorder(true); // Show border if custom border exists
            }
            // Load the thumbnail if it exists
            if (mapData.thumbnail) {
              setCurrentThumbnail(mapData.thumbnail);
            }
          }
        } catch (error) {
          console.error("Error loading map:", error);
          toast.error("Failed to load map");
        } finally {
          setLoading(false);
        }
      };
      loadMap();
    }
  }, [editMapId]);

  const handleStateCreated = (state: State) => {
    setStates((prev) => [...prev, state]);
    // Automatically select the newly created state so user can edit it immediately
    setSelectedState(state);
    // Exit drawing mode after creating a state
    setDrawingMode(false);
    // Show a helpful toast
    toast.success("State created! You can now edit its details below.");
  };

  const handleStateSelected = (state: State | null) => {
    setSelectedState(state);
    // Exit drawing mode when selecting a state
    if (state && drawingMode) {
      setDrawingMode(false);
    }
  };

  const handleStartEditing = (stateId: string | null) => {
    setEditingStateId(stateId);
    if (stateId) {
      setShowEditingInstructions(true); // Show instructions when starting to edit
    }
  };

  const handleFinishEditing = () => {
    setEditingStateId(null);
    setShowEditingInstructions(true); // Reset for next time
  };

  const handleStateUpdated = (state: State) => {
    setStates((prev) => prev.map((s) => (s.id === state.id ? state : s)));
  };

  const handleStatesEdited = (editedStates: State[]) => {
    // Filter out any border states from legacy data
    const regularStates = editedStates.filter(s => !s.isBorder);
    setStates(regularStates);
  };

  // Handler for editing custom border
  const handleStartEditingBorder = () => {
    if (!customBorder) {
      // If no custom border exists, initialize from region border
      const regionBorder = getRegionBorder(mapRegion);
      if (regionBorder) {
        const coords = convertBorderToCoordinates(regionBorder);
        setCustomBorder(coords);
      }
    }
    setIsEditingBorder(true);
    setShowBorder(true);
  };

  const handleBorderEdited = (newCoordinates: Coordinates[][]) => {
    setCustomBorder(newCoordinates);
  };

  const handleFinishEditingBorder = () => {
    setIsEditingBorder(false);
    toast.success("Border editing completed");
  };

  const handleDeleteBorder = () => {
    if (window.confirm("Delete custom border?")) {
      setCustomBorder(null);
      setIsEditingBorder(false);
      toast.success("Custom border deleted");
    }
  };

  // Helper to convert BorderFeature to Coordinates[][]
  const convertBorderToCoordinates = (border: any): Coordinates[][] => {
    if (border.geometry.type === "Polygon") {
      return (border.geometry.coordinates as number[][][]).map(ring =>
        ring.map(([lng, lat]) => ({ lat, lng }))
      );
    } else if (border.geometry.type === "MultiPolygon") {
      // Flatten MultiPolygon to single polygon array
      return (border.geometry.coordinates as number[][][][]).flatMap(polygon =>
        polygon.map(ring => ring.map(([lng, lat]) => ({ lat, lng })))
      );
    }
    return [];
  };

  const handleStateDeleted = (stateId: string) => {
    setStates((prev) => prev.filter((s) => s.id !== stateId));
    if (selectedState?.id === stateId) {
      setSelectedState(null);
    }
  };

  const handleAddPredefinedState = (state: Partial<State>) => {
    const newState: State = {
      id: Date.now().toString(),
      name: state.name || "New State",
      description: state.description || "",
      coordinates: state.coordinates || [],
      color: state.color || "#ff0000",
      capital: state.capital || "",
      population: state.population || 0,
      createdAt: new Date().toISOString(),
      ...state,
    };
    setStates((prev) => [...prev, newState]);
  };

  const handleTemplateSelect = useCallback((templateStates: any[]) => {
    const newStates = templateStates.map((state) => ({
      ...state,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString(),
    }));
    setStates((prev) => [...prev, ...newStates]);
  }, []);

  const handleSaveMap = async () => {
    if (!user) return;

    // Validate map name
    if (!mapName || mapName.trim() === "") {
      toast.error("Please enter a map name before saving.");
      return;
    }

    // Validate that at least one state layer exists
    if (states.length === 0) {
      toast.error("Please add at least one state layer before saving.");
      return;
    }

    // Check user limits before creating a new map
    if (!editMapId) {
      if (!canCreateMap()) {
        toast.error(
          "You have reached your map creation limit. Please upgrade your subscription to create more maps."
        );
        return;
      }
    }

    try {
      // Only save regular states (not borders)
      const allStatesToSave = states;

      const mapData = {
        name: mapName.trim(),
        description: mapDescription,
        states: allStatesToSave,
        guideLayers,
        isPublic,
        userId: user.id,
        userDisplayName: user.displayName,
        country: "Iran", // Default country
        tags: [], // Default empty tags
        thumbnail: currentThumbnail || undefined, // Include thumbnail in save data
        customBorder: customBorder || undefined, // Save custom border separately
      };

      let savedMapId = editMapId;

      if (editMapId) {
        // Update existing map
        await updateMap(editMapId, mapData);
        toast.success("Map updated successfully!");
      } else {
        // Create new map
        const createdMap = await createMap(mapData);
        savedMapId = createdMap.id;
        toast.success("Map created successfully!");

        // Update state immediately so version history can load
        setCurrentMapId(createdMap.id);

        // Update URL to include edit parameter
        navigate(`?edit=${createdMap.id}`, { replace: true });
      }

      await refreshUserMapsCount();

      // Refresh approval status and version (for both new and existing maps)
      if (savedMapId) {
        const updatedMap = await getMapById(savedMapId);
        if (updatedMap) {
          setApprovalRequested(updatedMap.approvalRequested || false);
          setIsApproved(updatedMap.isApproved || false);
          setIsRejected(updatedMap.isRejected || false);
          setRejectionReason(updatedMap.rejectionReason);
          setVersion(updatedMap.version);
          setApprovedVersion(updatedMap.approvedVersion);
        }
      }
      // Stay on the current page after saving (don't redirect to /my-maps)
    } catch (error) {
      console.error("Error saving map:", error);
      toast.error("Failed to save map");
    }
  };

  // Validation function for approval requirements (CreateMapPage version)
  const validateApprovalRequirements = () => {
    const issues: string[] = [];

    // Check if map has a proper name
    if (!mapName || mapName.trim() === "" || mapName === "Untitled Map") {
      issues.push("❌ Map name must be changed from default");
    } else {
      issues.push("✅ Map has a proper name");
    }

    // Check if map has a thumbnail
    if (!currentThumbnail) {
      issues.push("❌ Map thumbnail is required");
    } else {
      issues.push("✅ Map has a thumbnail");
    }

    // Check if map has a description
    if (!mapDescription || mapDescription.trim() === "") {
      issues.push("❌ Map description is required");
    } else {
      issues.push("✅ Map has a description");
    }

    // Check if map has at least one state
    if (states.length === 0) {
      issues.push("❌ At least one state layer is required");
    } else {
      issues.push("✅ Map has state layers");
    }

    // Check each state for proper name and capital
    states.forEach((state, index) => {
      // Check if state name is auto-generated
      if (state.name === "New State" || state.name.match(/^New State \d+$/)) {
        issues.push(`❌ State ${index + 1}: "New State" name must be changed`);
      } else {
        issues.push(`✅ State ${index + 1}: "${state.name}" has proper name`);
      }

      // Check if state has a capital
      if (!state.capital || state.capital.trim() === "") {
        issues.push(`❌ State ${index + 1}: Capital is required`);
      } else {
        issues.push(`✅ State ${index + 1}: Capital "${state.capital}" is set`);
      }
    });

    return issues;
  };

  // Request approval handler for CreateMapPage (used by ValidationModal)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRequestApproval = async () => {
    if (!editMapId || !user) {
      toast.error("Please save your map first before requesting approval");
      return;
    }

    // Validate approval requirements
    const issues = validateApprovalRequirements();
    const hasErrors = issues.some((issue) => issue.startsWith("❌"));

    // Show validation modal
    setValidationIssues(issues);
    setValidationHasErrors(hasErrors);
    setIsValidationModalOpen(true);
  };

  const handleProceedWithApproval = async () => {
    if (!editMapId) return;
    try {
      await requestMapApproval(editMapId);
      toast.success(
        "Approval request submitted! An admin will review your map."
      );
      // Refresh approval status and version
      const updatedMap = await getMapById(editMapId);
      if (updatedMap) {
        setApprovalRequested(updatedMap.approvalRequested || false);
        setIsApproved(updatedMap.isApproved || false);
        setIsRejected(updatedMap.isRejected || false);
        setRejectionReason(updatedMap.rejectionReason);
        setVersion(updatedMap.version);
        setApprovedVersion(updatedMap.approvedVersion);
      }
      setIsValidationModalOpen(false);
    } catch (error) {
      toast.error("Failed to request approval");
      console.error("Error requesting approval:", error);
    }
  };

  const handleExportMap = () => {
    const mapData = {
      apiVersion: "v1",
      kind: "MapTemplate",
      id: editMapId || `custom-${Date.now()}`,
      name: mapName,
      description: mapDescription,
      createdAt: new Date().toISOString(),
      states,
      guideLayers: guideLayers.length > 0 ? guideLayers : undefined,
      customBorder: customBorder || undefined, // Export custom border
      region: mapRegion, // Export region
      tags: [], // Empty tags array by default
      // Include version info if editing an existing map
      ...(editMapId && { exportedAt: new Date().toISOString() }),
    };
    const dataStr = JSON.stringify(mapData, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportFileDefaultName = `${mapName || "map"}.json`;
    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
    toast.success("Map exported as JSON successfully!");
  };

  const handleImportMap = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        if (typeof text !== "string") {
          throw new Error("Invalid file content");
        }
        const importedData = JSON.parse(text);

        // Check if this is a full map export (has name and states)
        if (
          importedData.name &&
          importedData.states &&
          Array.isArray(importedData.states)
        ) {
          // Validate schema
          const validation = validateMapSchema(importedData);
          
          if (!validation.isValid) {
            toast.error(getValidationMessage(validation));
            return;
          }
          
          // Show warnings if any
          if (validation.warnings.length > 0) {
            console.warn("Import warnings:", validation.warnings);
          }
          
          // Full map import - replace everything
          handleTemplateImport(importedData, true);
        } else if (
          Array.isArray(importedData) &&
          importedData.every((s) => "id" in s && "name" in s)
        ) {
          // Array of states only - append
          setStates((prev) => [...prev, ...importedData]);
          setSelectedState(null);
          toast.success(
            `Added ${importedData.length} state${
              importedData.length !== 1 ? "s" : ""
            } from file`
          );
        } else if (
          importedData.guideLayers &&
          Array.isArray(importedData.guideLayers) &&
          !importedData.states
        ) {
          // Guide layers only - append
          setGuideLayers((prev) => [...prev, ...importedData.guideLayers]);
          toast.success(
            `Added ${importedData.guideLayers.length} guide layer${
              importedData.guideLayers.length !== 1 ? "s" : ""
            } from file`
          );
        } else {
          toast.error("Invalid file format");
        }
      } catch (error) {
        console.error("Error importing map:", error);
        toast.error("Could not import map");
      }
    };
    reader.readAsText(file);
    // Reset file input so same file can be imported again
    event.target.value = "";
  };

  const handleTemplateImport = (
    templateData: any,
    replaceAll: boolean = false
  ) => {
    // Generate new IDs for states to avoid conflicts
    const newStates = (templateData.states || []).map((state: any) => ({
      ...state,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString(),
    }));

    // Replace all states if this is a full map import, otherwise append
    if (replaceAll) {
      setStates(newStates);
      // Restore guide layers if they exist and are valid
      if (
        templateData.guideLayers &&
        Array.isArray(templateData.guideLayers) &&
        templateData.guideLayers.length > 0
      ) {
        setGuideLayers(templateData.guideLayers);
      } else {
        setGuideLayers([]);
      }
      // Import custom border if it exists
      if (templateData.customBorder) {
        setCustomBorder(templateData.customBorder);
        setShowBorder(true);
      } else {
        setCustomBorder(null);
      }
      // Import region if it exists
      if (templateData.region) {
        setMapRegion(templateData.region);
      }
      setMapName(templateData.name || "Imported Map");
      setMapDescription(templateData.description || "");
      setSelectedState(null);
      const guideLayerCount = templateData.guideLayers?.length || 0;
      toast.success(
        `Imported map: ${templateData.name}${
          guideLayerCount > 0
            ? ` (${guideLayerCount} guide layer${
                guideLayerCount !== 1 ? "s" : ""
              })`
            : ""
        }`
      );
    } else {
      // Append mode (for template imports)
      setStates((prev) => [...prev, ...newStates]);
      if (
        templateData.guideLayers &&
        Array.isArray(templateData.guideLayers) &&
        templateData.guideLayers.length > 0
      ) {
        setGuideLayers((prev) => [...prev, ...templateData.guideLayers]);
      }
      setMapName(templateData.name || "Imported Map");
      setMapDescription(templateData.description || "");
      toast.success(`Imported template: ${templateData.name}`);
    }
  };

  const handleClearAllStates = () => {
    if (window.confirm("Are you sure you want to clear all states?")) {
      setStates([]);
      setSelectedState(null);
    }
  };

  const handleAddGuideLayer = (guideLayer: GuideLayer) => {
    setGuideLayers((prev) => [...prev, guideLayer]);
  };

  const handleUpdateGuideLayer = (id: string, updates: Partial<GuideLayer>) => {
    setGuideLayers((prev) =>
      prev.map((layer) => (layer.id === id ? { ...layer, ...updates } : layer))
    );
  };

  const handleDeleteGuideLayer = (id: string) => {
    setGuideLayers((prev) => prev.filter((layer) => layer.id !== id));
  };

  const handleToggleGuideLayer = (id: string) => {
    setGuideLayers((prev) =>
      prev.map((layer) =>
        layer.id === id ? { ...layer, visible: !layer.visible } : layer
      )
    );
  };

  const handleCapitalCoordinateSet = (stateId: string, coordinates: any) => {
    setStates((prev) =>
      prev.map((state) =>
        state.id === stateId
          ? { ...state, capitalCoordinates: coordinates }
          : state
      )
    );

    // Also update the StatePanel's tempState if it's editing the same state
    if (statePanelRef.current?.handleCapitalCoordinateSet) {
      statePanelRef.current.handleCapitalCoordinateSet(stateId, coordinates);
    }
  };

  const handleCustomThumbnailFile = async (file: File | null) => {
    if (!file) return;

    try {
      // Show loading toast
      const loadingToast = toast.loading("Uploading thumbnail...");

      // For new maps, we'll upload with a temporary ID and update it later
      // For existing maps, we can upload directly
      const mapId = editMapId || `temp-${Date.now()}`;

      // Optimize the image first
      const optimizedFile = await prepareThumbnailForUpload(file);

      // Upload to Firebase Storage and get the URL
      const thumbnailUrl = await uploadMapThumbnail(mapId, optimizedFile);

      // Set the thumbnail URL
      setCurrentThumbnail(thumbnailUrl);

      toast.dismiss(loadingToast);
      toast.success("Thumbnail uploaded successfully!");
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      toast.error("Failed to upload thumbnail");
    }
  };

  return (
    <MapContainer $isPanelOpen={isPanelOpen}>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            fontSize: "18px",
            color: "#666",
          }}
        >
          Loading map for editing...
        </div>
      ) : (
        <>
          <MapArea $isPanelOpen={isPanelOpen} className="map-area">
            <MapComponent
              ref={mapRef}
              states={states}
              guideLayers={guideLayers}
              drawingMode={drawingMode}
              selectedState={selectedState}
              editingStateId={editingStateId}
              showEditingInstructions={showEditingInstructions}
              onStateCreated={handleStateCreated}
              onStateSelected={handleStateSelected}
              onStatesEdited={handleStatesEdited}
              onFinishEditing={handleFinishEditing}
              onHideInstructions={() => setShowEditingInstructions(false)}
              showCapitals={showCapitals}
              showCapitalNames={showCapitalNames}
              showStateNames={showStateNames}
              showStateFill={showStateFill}
              mapStyle={mapStyle}
              onCapitalCoordinateSet={handleCapitalCoordinateSet}
              isSettingCapital={isSettingCapital}
              editingGuideLayerId={editingGuideLayerId}
              selectedGuideLayer={
                statePanelRef.current?.getSelectedGuideLayer?.() || null
              }
              onGuideLayerTransform={handleUpdateGuideLayer}
              regionCode={mapRegion}
              showBorder={showBorder}
              customBorder={customBorder}
              isEditingBorder={isEditingBorder}
              onBorderEdited={handleBorderEdited}
            />

            {/* Mobile Toggle Button */}
            <PanelToggleButton
              $isOpen={isPanelOpen}
              $isMobile={true}
              onClick={() => setIsPanelOpen(!isPanelOpen)}
              title={isPanelOpen ? "Hide Panel" : "Show Panel"}
            >
              {isPanelOpen ? "▼" : "▲"}
            </PanelToggleButton>

            {/* Desktop Toggle Button */}
            <PanelToggleButton
              $isOpen={isPanelOpen}
              $isMobile={false}
              onClick={() => setIsPanelOpen(!isPanelOpen)}
              title={isPanelOpen ? "Hide Panel" : "Show Panel"}
            >
              {isPanelOpen ? "▶" : "◀"}
            </PanelToggleButton>
          </MapArea>
          <Sidebar $isOpen={isPanelOpen}>
            <StatePanel
              ref={statePanelRef}
              states={states}
              selectedState={selectedState}
              onStateSelect={setSelectedState}
              onStateUpdate={handleStateUpdated}
              onStateDelete={handleStateDeleted}
              onAddPredefinedState={handleAddPredefinedState}
              onTemplateSelect={handleTemplateSelect}
              editingStateId={editingStateId}
              setEditingStateId={handleStartEditing}
              drawingMode={drawingMode}
              setDrawingMode={setDrawingMode}
              onSaveMap={handleSaveMap}
              onExportTemplate={handleExportMap}
              onImportTemplate={handleImportMap}
              onClearAllStates={handleClearAllStates}
              guideLayers={guideLayers}
              onAddGuideLayer={handleAddGuideLayer}
              onUpdateGuideLayer={handleUpdateGuideLayer}
              onDeleteGuideLayer={handleDeleteGuideLayer}
              onToggleGuideLayer={handleToggleGuideLayer}
              mapName={mapName}
              setMapName={setMapName}
              mapDescription={mapDescription}
              setMapDescription={setMapDescription}
              mapRegion={mapRegion}
              onRegionChange={handleRegionChange}
              isPublic={isPublic}
              setIsPublic={setIsPublic}
              showCapitals={showCapitals}
              setShowCapitals={setShowCapitals}
              showCapitalNames={showCapitalNames}
              setShowCapitalNames={setShowCapitalNames}
              showStateNames={showStateNames}
              setShowStateNames={setShowStateNames}
              showStateFill={showStateFill}
              setShowStateFill={setShowStateFill}
              mapStyle={mapStyle}
              setMapStyle={setMapStyle}
              isEditMode={!!editMapId}
              isSettingCapital={isSettingCapital}
              setIsSettingCapital={setIsSettingCapital}
              onCapitalCoordinateSet={handleCapitalCoordinateSet}
              setEditingGuideLayerId={setEditingGuideLayerId}
              mapRef={mapRef}
              currentThumbnail={currentThumbnail || undefined}
              onCustomThumbnailFile={handleCustomThumbnailFile}
              mapId={currentMapId || undefined}
              approvalRequested={approvalRequested} // Used in StatePanel
              isApproved={isApproved} // Used in StatePanel
              isRejected={isRejected} // Used in StatePanel
              rejectionReason={rejectionReason} // Used in StatePanel
              version={version} // Used in StatePanel
              approvedVersion={approvedVersion} // Used in StatePanel
              onRestoreVersion={(versionData) => {
                // Load version data to the map (for viewing/navigation)
                // Keep original IDs to allow proper restoration
                // Filter out any border states from legacy versions
                const versionStates = (versionData.states || []).filter(s => !s.isBorder);
                
                setStates(versionStates);
                setGuideLayers(versionData.guideLayers || []);
                setMapName(versionData.name);
                setMapDescription(versionData.description || "");
                // Restore thumbnail from the version (or clear it if version has none)
                // Explicitly check for truthy value to handle empty strings, null, undefined
                setCurrentThumbnail(
                  versionData.thumbnail && versionData.thumbnail.trim() !== ""
                    ? versionData.thumbnail
                    : null
                );
                setSelectedState(null);
              }}
              // Border props
              showBorder={showBorder}
              setShowBorder={setShowBorder}
              customBorder={customBorder}
              isEditingBorder={isEditingBorder}
              onStartEditingBorder={handleStartEditingBorder}
              onFinishEditingBorder={handleFinishEditingBorder}
              onDeleteBorder={handleDeleteBorder}
            />
          </Sidebar>

          <ValidationModal
            isOpen={isValidationModalOpen}
            onClose={() => setIsValidationModalOpen(false)}
            onProceed={handleProceedWithApproval} // handleRequestApproval triggers this modal
            validationIssues={validationIssues}
            hasErrors={validationHasErrors}
          />

          <TemplateExportModal
            isOpen={isTemplateExportModalOpen}
            onClose={() => setIsTemplateExportModalOpen(false)}
            states={states}
            guideLayers={guideLayers}
          />
        </>
      )}
    </MapContainer>
  );
};

// View Map Page Component
const ViewMapPage: React.FC = () => {
  const { mapId } = useParams<{ mapId: string }>();
  const { isDarkMode } = useTheme();
  const [map, setMap] = useState<MapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationIssues, setValidationIssues] = useState<string[]>([]);
  const [validationHasErrors, setValidationHasErrors] = useState(false);
  const { user, refreshUserMapsCount } = useAuth();
  const navigate = useNavigate();
  const mapRef = useRef<any>(null);
  const [showCapitals, setShowCapitals] = useState(true);
  const [showCapitalNames, setShowCapitalNames] = useState(false);
  const [showStateNames, setShowStateNames] = useState(false);
  const [showStateFill, setShowStateFill] = useState(true);
  const [mapStyle, setMapStyle] = useState<MapStyle>("light");
  const [isDisplayOptionsExpanded, setIsDisplayOptionsExpanded] =
    useState(true);
  const [isMapStyleExpanded, setIsMapStyleExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<"info" | "settings">("info");

  // Trigger map resize when panel state changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current && mapRef.current.resize) {
        try {
          mapRef.current.resize();
        } catch (error) {
          // Silently handle resize errors
        }
      }
    }, 300); // Wait for transition to complete

    return () => clearTimeout(timer);
  }, [isPanelOpen]);

  React.useEffect(() => {
    const loadMap = async () => {
      if (!mapId) return;

      try {
        const mapData = await getMapById(mapId);
        if (mapData) {
          setMap(mapData);
          setLikesCount(mapData.likesCount);
        }
      } catch (error) {
        console.error("Error loading map:", error);
      } finally {
        setLoading(false);
      }
    };

    loadMap();
  }, [mapId, user]);

  const handleLike = async () => {
    if (!user || !mapId) return;

    try {
      if (isLiked) {
        await unlikeMap(mapId, user.id);
        setIsLiked(false);
        setLikesCount((prev) => prev - 1);
      } else {
        await likeMap(mapId, user.id);
        setIsLiked(true);
        setLikesCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  // Function to refresh map data (for comment count updates)
  const refreshMapData = async () => {
    if (!mapId) return;

    try {
      const mapData = await getMapById(mapId);
      if (mapData) {
        setMap(mapData);
        setLikesCount(mapData.likesCount);
      }
    } catch (error) {
      console.error("Error refreshing map data:", error);
    }
  };

  // Delete map handler
  const handleDeleteMap = async () => {
    if (!mapId || !user || !map) return;
    if (
      !window.confirm(
        "Are you sure you want to delete this map? This cannot be undone."
      )
    )
      return;
    try {
      await deleteMap(mapId);
      toast.success("Map deleted successfully!");

      // Refresh user's maps count after deleting a map
      await refreshUserMapsCount();

      navigate("/my-maps");
    } catch (error) {
      toast.error("Failed to delete map");
      console.error("Error deleting map:", error);
    }
  };

  // Validation function for approval requirements (ViewMapPage version)
  const validateApprovalRequirementsView = () => {
    const issues: string[] = [];

    if (!map) {
      issues.push("❌ Map data not available");
      return issues;
    }

    // Check if map has a proper name (not default "Untitled Map")
    if (!map.name || map.name.trim() === "" || map.name === "Untitled Map") {
      issues.push("❌ Map name must be changed from default");
    } else {
      issues.push("✅ Map has a proper name");
    }

    // Check if map has a thumbnail
    if (!map.thumbnail) {
      issues.push("❌ Map thumbnail is required");
    } else {
      issues.push("✅ Map has a thumbnail");
    }

    // Check if map has a description
    if (!map.description || map.description.trim() === "") {
      issues.push("❌ Map description is required");
    } else {
      issues.push("✅ Map has a description");
    }

    // Check if map has at least one state
    if (map.states.length === 0) {
      issues.push("❌ At least one state layer is required");
    } else {
      issues.push("✅ Map has state layers");
    }

    // Check each state for proper name and capital
    map.states.forEach((state, index) => {
      // Check if state name is auto-generated (default "New State" or "New State" with timestamp)
      if (state.name === "New State" || state.name.match(/^New State \d+$/)) {
        issues.push(`❌ State ${index + 1}: "New State" name must be changed`);
      } else {
        issues.push(`✅ State ${index + 1}: "${state.name}" has proper name`);
      }

      // Check if state has a capital
      if (!state.capital || state.capital.trim() === "") {
        issues.push(`❌ State ${index + 1}: Capital is required`);
      } else {
        issues.push(`✅ State ${index + 1}: Capital "${state.capital}" is set`);
      }
    });

    return issues;
  };

  // Request approval handler
  const handleRequestApproval = async () => {
    if (!mapId || !user || !map) return;

    // Validate approval requirements
    const issues = validateApprovalRequirementsView();
    const hasErrors = issues.some((issue) => issue.startsWith("❌"));

    // Show validation modal
    setValidationIssues(issues);
    setValidationHasErrors(hasErrors);
    setIsValidationModalOpen(true);
  };

  const handleProceedWithApproval = async () => {
    try {
      await requestMapApproval(mapId!);
      toast.success(
        "Approval request submitted! An admin will review your map."
      );
      // Refresh map data to show updated status
      const updatedMap = await getMapById(mapId!);
      if (updatedMap) {
        setMap(updatedMap);
      }
      setIsValidationModalOpen(false);
    } catch (error) {
      toast.error("Failed to request approval");
      console.error("Error requesting approval:", error);
    }
  };

  // Make approved map public handler
  const handleMakePublic = async () => {
    if (!mapId || !user || !map) return;
    try {
      await updateMap(mapId, { isPublic: true });
      toast.success("Map is now public and visible to everyone!");
      // Refresh map data to show updated status
      const updatedMap = await getMapById(mapId);
      if (updatedMap) {
        setMap(updatedMap);
      }
    } catch (error) {
      toast.error("Failed to make map public");
      console.error("Error making map public:", error);
    }
  };

  if (loading) {
    return <div>Loading map...</div>;
  }

  if (!map) {
    return <div>Map not found</div>;
  }

  return (
    <MapContainer $isPanelOpen={isPanelOpen}>
      <MapArea $isPanelOpen={isPanelOpen} className="map-area">
        <MapComponent
          ref={mapRef}
          states={map.states}
          guideLayers={map.guideLayers || []}
          drawingMode={false}
          selectedState={null}
          editingStateId={null}
          showEditingInstructions={true}
          onStateCreated={() => {}}
          onStateSelected={() => {}}
          onStatesEdited={() => {}}
          onFinishEditing={() => {}}
          onHideInstructions={() => {}}
          showCapitals={showCapitals}
          showCapitalNames={showCapitalNames}
          showStateNames={showStateNames}
          showStateFill={showStateFill}
          mapStyle={mapStyle}
          onCapitalCoordinateSet={() => {}}
          isSettingCapital={false}
          editingGuideLayerId={null}
          selectedGuideLayer={null}
          onGuideLayerTransform={() => {}}
          regionCode={map.region}
          showBorder={false}
        />

        {/* Mobile Toggle Button */}
        <PanelToggleButton
          $isOpen={isPanelOpen}
          $isMobile={true}
          onClick={() => setIsPanelOpen(!isPanelOpen)}
          title={isPanelOpen ? "Hide Panel" : "Show Panel"}
        >
          {isPanelOpen ? "▼" : "▲"}
        </PanelToggleButton>

        {/* Desktop Toggle Button */}
        <PanelToggleButton
          $isOpen={isPanelOpen}
          $isMobile={false}
          onClick={() => setIsPanelOpen(!isPanelOpen)}
          title={isPanelOpen ? "Hide Panel" : "Show Panel"}
        >
          {isPanelOpen ? "▶" : "◀"}
        </PanelToggleButton>
      </MapArea>
      <SocialSidebar $isOpen={isPanelOpen} $isDarkMode={isDarkMode}>
        <ViewPanelContent $isDarkMode={isDarkMode}>
          <ViewTabsContainer $isDarkMode={isDarkMode}>
            <ViewTabButton
              $isActive={activeTab === "info"}
              $isDarkMode={isDarkMode}
              onClick={() => setActiveTab("info")}
            >
              Info
            </ViewTabButton>
            <ViewTabButton
              $isActive={activeTab === "settings"}
              $isDarkMode={isDarkMode}
              onClick={() => setActiveTab("settings")}
            >
              Settings
            </ViewTabButton>
          </ViewTabsContainer>

          {/* Info Tab */}
          {activeTab === "info" && (
            <ViewTabContent $isDarkMode={isDarkMode}>
              <ViewSection $isDarkMode={isDarkMode}>
                <ViewSectionHeader $isDarkMode={isDarkMode}>
                  <ViewSectionIcon>🗺️</ViewSectionIcon>
                  Map Information
                </ViewSectionHeader>
                <ViewMapTitle $isDarkMode={isDarkMode}>{map.name}</ViewMapTitle>
                {map.description && (
                  <ViewMapDescription $isDarkMode={isDarkMode}>
                    {map.description}
                  </ViewMapDescription>
                )}

                {/* Region Badge */}
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.375rem 0.75rem",
                    background: isDarkMode ? "#2d3748" : "#e2e8f0",
                    borderRadius: "9999px",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    color: isDarkMode ? "#e2e8f0" : "#4a5568",
                    marginBottom: "1rem",
                  }}
                >
                  <span>🌍</span>
                  <span>Iran</span>
                </div>

                <ViewUserInfo>
                  <ViewUserAvatar
                    $avatar={map.userAvatar}
                    $isDarkMode={isDarkMode}
                  >
                    {!map.userAvatar &&
                      map.userDisplayName.charAt(0).toUpperCase()}
                  </ViewUserAvatar>
                  <ViewUserName $isDarkMode={isDarkMode}>
                    {map.userDisplayName}
                  </ViewUserName>
                </ViewUserInfo>

                <button
                  onClick={handleLike}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    background: "none",
                    border: "none",
                    color: isLiked ? "#e74c3c" : "#666",
                    cursor: "pointer",
                    padding: "0.5rem",
                    borderRadius: "6px",
                    transition: "all 0.2s",
                    fontSize: "0.9rem",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.background = isLiked
                      ? "#fdf2f2"
                      : "#f8f9fa")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                >
                  <svg
                    width="16"
                    height="16"
                    fill={isLiked ? "currentColor" : "none"}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                  {likesCount} {likesCount === 1 ? "like" : "likes"}
                </button>
              </ViewSection>

              {/* Actions Section */}
              {user && map.userId === user.id && (
                <ViewSection $isDarkMode={isDarkMode}>
                  <ViewSectionHeader $isDarkMode={isDarkMode}>
                    <ViewSectionIcon>⚙️</ViewSectionIcon>
                    Actions
                  </ViewSectionHeader>
                  <ViewButtonGroup>
                    <ViewPrimaryButton
                      $isDarkMode={isDarkMode}
                      onClick={() => navigate(`/create?edit=${map.id}`)}
                    >
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit Map
                    </ViewPrimaryButton>

                    {/* Request Approval button for private maps */}
                    {!map.isPublic &&
                      !map.approvalRequested &&
                      !map.isRejected &&
                      !map.isApproved &&
                      user?.role !== "admin" &&
                      user?.role !== "approver" && (
                        <ViewSecondaryButton
                          $isDarkMode={isDarkMode}
                          onClick={handleRequestApproval}
                        >
                          <svg
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Request Approval
                        </ViewSecondaryButton>
                      )}

                    {/* Approval Status */}
                    {!map.isPublic &&
                      map.approvalRequested &&
                      !map.isRejected &&
                      !map.isApproved && (
                        <ViewStatusBadge
                          $variant="warning"
                          $isDarkMode={isDarkMode}
                        >
                          ⏳ Pending Approval
                        </ViewStatusBadge>
                      )}

                    {map.isRejected && (
                      <ViewStatusBadge
                        $variant="danger"
                        $isDarkMode={isDarkMode}
                      >
                        ❌ Rejected
                        {map.rejectionReason && (
                          <div
                            style={{
                              fontSize: "11px",
                              marginTop: "4px",
                              opacity: 0.9,
                            }}
                          >
                            {map.rejectionReason}
                          </div>
                        )}
                      </ViewStatusBadge>
                    )}

                    {map.isApproved && !map.isPublic && (
                      <ViewSecondaryButton
                        $isDarkMode={isDarkMode}
                        onClick={handleMakePublic}
                      >
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Make Public
                      </ViewSecondaryButton>
                    )}

                    <ViewDangerButton
                      $isDarkMode={isDarkMode}
                      onClick={handleDeleteMap}
                    >
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Delete Map
                    </ViewDangerButton>
                  </ViewButtonGroup>
                </ViewSection>
              )}

              {/* Comments Section */}
              <ViewSection $isDarkMode={isDarkMode}>
                <ViewSectionHeader $isDarkMode={isDarkMode}>
                  <ViewSectionIcon>💬</ViewSectionIcon>
                  Comments
                </ViewSectionHeader>
                <CommentsSection
                  mapId={mapId!}
                  onCommentAdded={refreshMapData}
                  onCommentsCountChange={() => {}}
                />
              </ViewSection>
            </ViewTabContent>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && (
            <ViewTabContent $isDarkMode={isDarkMode}>
              {/* Display Options Section */}
              <ViewSection $isDarkMode={isDarkMode} $compact>
                <ViewSectionHeader
                  $isDarkMode={isDarkMode}
                  $compact
                  $clickable
                  onClick={() =>
                    setIsDisplayOptionsExpanded(!isDisplayOptionsExpanded)
                  }
                >
                  <ViewSectionIcon>🎨</ViewSectionIcon>
                  Display Options
                  <ViewSectionChevron
                    $isExpanded={isDisplayOptionsExpanded}
                    $isDarkMode={isDarkMode}
                  >
                    ▼
                  </ViewSectionChevron>
                </ViewSectionHeader>
                {isDisplayOptionsExpanded && (
                  <div style={{ marginTop: "4px" }}>
                    <ViewToggleContainer $compact>
                      <ViewToggleLabel
                        $isDarkMode={isDarkMode}
                        $compact
                        htmlFor="view-capitals-toggle"
                      >
                        Show capital pins
                      </ViewToggleLabel>
                      <ViewToggleSwitch
                        $checked={showCapitals}
                        $isDarkMode={isDarkMode}
                        $compact
                        htmlFor="view-capitals-toggle"
                      >
                        <ViewToggleInput
                          type="checkbox"
                          id="view-capitals-toggle"
                          checked={showCapitals}
                          onChange={(e) => setShowCapitals(e.target.checked)}
                        />
                      </ViewToggleSwitch>
                    </ViewToggleContainer>
                    <ViewToggleContainer $compact>
                      <ViewToggleLabel
                        $isDarkMode={isDarkMode}
                        $compact
                        htmlFor="view-capital-names-toggle"
                      >
                        Show capital name
                      </ViewToggleLabel>
                      <ViewToggleSwitch
                        $checked={showCapitalNames}
                        $isDarkMode={isDarkMode}
                        $compact
                        htmlFor="view-capital-names-toggle"
                      >
                        <ViewToggleInput
                          type="checkbox"
                          id="view-capital-names-toggle"
                          checked={showCapitalNames}
                          onChange={(e) =>
                            setShowCapitalNames(e.target.checked)
                          }
                        />
                      </ViewToggleSwitch>
                    </ViewToggleContainer>
                    <ViewToggleContainer $compact>
                      <ViewToggleLabel
                        $isDarkMode={isDarkMode}
                        $compact
                        htmlFor="view-state-names-toggle"
                      >
                        Show state name
                      </ViewToggleLabel>
                      <ViewToggleSwitch
                        $checked={showStateNames}
                        $isDarkMode={isDarkMode}
                        $compact
                        htmlFor="view-state-names-toggle"
                      >
                        <ViewToggleInput
                          type="checkbox"
                          id="view-state-names-toggle"
                          checked={showStateNames}
                          onChange={(e) => setShowStateNames(e.target.checked)}
                        />
                      </ViewToggleSwitch>
                    </ViewToggleContainer>
                    <ViewToggleContainer $compact>
                      <ViewToggleLabel
                        $isDarkMode={isDarkMode}
                        $compact
                        htmlFor="view-state-fill-toggle"
                      >
                        Show state fill colors
                      </ViewToggleLabel>
                      <ViewToggleSwitch
                        $checked={showStateFill !== false}
                        $isDarkMode={isDarkMode}
                        $compact
                        htmlFor="view-state-fill-toggle"
                      >
                        <ViewToggleInput
                          type="checkbox"
                          id="view-state-fill-toggle"
                          checked={showStateFill !== false}
                          onChange={(e) => setShowStateFill(e.target.checked)}
                        />
                      </ViewToggleSwitch>
                    </ViewToggleContainer>
                  </div>
                )}
              </ViewSection>

              {/* Map Style Section */}
              <ViewSection $isDarkMode={isDarkMode} $compact>
                <ViewSectionHeader
                  $isDarkMode={isDarkMode}
                  $compact
                  $clickable
                  onClick={() => setIsMapStyleExpanded(!isMapStyleExpanded)}
                >
                  <ViewSectionIcon>🗺️</ViewSectionIcon>
                  Map Style
                  <ViewSectionChevron
                    $isExpanded={isMapStyleExpanded}
                    $isDarkMode={isDarkMode}
                  >
                    ▼
                  </ViewSectionChevron>
                </ViewSectionHeader>
                {isMapStyleExpanded && (
                  <MapStyleGallery $compact>
                    {Object.entries(MAP_STYLES).map(([key, style]) => (
                      <MapStyleCard
                        key={key}
                        $isSelected={mapStyle === key}
                        $isDarkMode={isDarkMode}
                        $color={style.color}
                        $compact
                        onClick={() => setMapStyle(key as MapStyle)}
                        title={style.description}
                      >
                        <MapStylePreview
                          $color={style.color}
                          $thumbnail={style.thumbnail}
                        />
                        <MapStyleLabel $isDarkMode={isDarkMode} $compact>
                          {style.name}
                        </MapStyleLabel>
                      </MapStyleCard>
                    ))}
                  </MapStyleGallery>
                )}
              </ViewSection>
            </ViewTabContent>
          )}
        </ViewPanelContent>
      </SocialSidebar>

      <ValidationModal
        isOpen={isValidationModalOpen}
        onClose={() => setIsValidationModalOpen(false)}
        onProceed={handleProceedWithApproval}
        validationIssues={validationIssues}
        hasErrors={validationHasErrors}
      />
    </MapContainer>
  );
};

const AppContent: React.FC = () => {
  return (
    <AppContainer>
      <SEOHead />
      <Navigation />
      <MainContent>
        <Routes>
          {/* English Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <CreateMapPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-map"
            element={
              <ProtectedRoute>
                <CreateMapPage />
              </ProtectedRoute>
            }
          />
          <Route path="/map/:mapId" element={<ViewMapPage />} />
          <Route path="/my-maps" element={<MyMapsPage />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin-panel"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/approver"
            element={
              <ApproverRoute>
                <ApproverDashboard />
              </ApproverRoute>
            }
          />
          <Route
            path="/approver-panel"
            element={
              <ApproverRoute>
                <ApproverDashboard />
              </ApproverRoute>
            }
          />

          {/* Multi-language Routes */}
          <Route path="/fa" element={<HomePage />} />
          <Route path="/fa/faq" element={<FAQPage />} />
          <Route path="/ku" element={<HomePage />} />
          <Route path="/ku/faq" element={<FAQPage />} />
          <Route path="/ar" element={<HomePage />} />
          <Route path="/ar/faq" element={<FAQPage />} />
          <Route path="/fr" element={<HomePage />} />
          <Route path="/bal" element={<HomePage />} />
          <Route path="/mzn" element={<HomePage />} />
          <Route path="/glk" element={<HomePage />} />
          <Route path="/az" element={<HomePage />} />
          <Route path="/az/faq" element={<FAQPage />} />
        </Routes>
      </MainContent>
      <CookieConsentBanner />
      <Toaster position="top-right" />
    </AppContainer>
  );
};

// Root App Component with Providers
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <I18nProvider>
            <AppContent />
          </I18nProvider>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
